import React, { Dispatch, ReactNode, useEffect, useMemo, useState } from 'react';
import { Logo } from '@components/Icons';
import cn from 'classnames';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import {
  AppleAuthCredential,
  AuthPlatform,
  checkStorageForTemporaryAccess,
  clearAuthorizationError,
  completeAuthWorkflow,
  completeEmailAuthWorkflow,
  completeSignInWithAppleAuthWorkflow,
  getAuthorizationError,
  previousAuthPlatform,
} from '@support/jwt';
import { lastValueFrom } from 'rxjs';
import LoginErrorModal from '../ErrorModal';
import styles from './LoginPanel.module.scss';
import { useLocation, useSearchParams } from 'react-router-dom';
import AuthOptions from '../AuthOptions';
import EmailConfirmation from '../EmailConfirmation';
import envelopeImg from '@app/img/login/envelope.png';
import missingAccountImg from '@app/img/login/missing_account.png';
import FooterLinks from '@components/FooterLinks';
import { ToggleButton } from '../Buttons';
import { supportHref } from '@support/preferred-email-client';

type ViewState =
  | 'existingAccount'
  | 'newAccount'
  | 'loginEmailSent'
  | 'accountNotFound'
  | 'emailAlreadyInUse';
type LoginError = { error: string; provider?: AuthPlatform };

const attachDesktopLoginHandlers = (
  setLoginError: Dispatch<LoginError | null>,
  setSigningInWith: Dispatch<'google' | 'apple' | 'email' | null>,
) => {
  const handleErrorMsg = (error) => setLoginError(error);
  const clearSigningInWith = () => setSigningInWith(null);
  const completeLogin = () => (window.location.href = '/');

  function handleDesktopLoginCallback({
    email,
    token,
    register,
  }: { email?: string; token?: string; register?: boolean } = {}) {
    if (email && token) {
      lastValueFrom(completeEmailAuthWorkflow(token, window.atob(email), register)).then(
        completeLogin,
      );
      return;
    }

    const access = checkStorageForTemporaryAccess();
    if (!access) {
      console.warn('login:callback called but no TemporaryAccess data was found.');
      return;
    }

    lastValueFrom(completeAuthWorkflow(access.token, access.provider)).then(completeLogin);
  }

  function handleFinishLoginOnMacWithApple({
    credential,
    isRegister,
  }: {
    credential: AppleAuthCredential;
    isRegister: boolean;
  }) {
    lastValueFrom(completeSignInWithAppleAuthWorkflow(credential, isRegister))
      .then(completeLogin)
      .catch(clearSigningInWith);
  }

  const unsub1 = window.desktopIntegrations.onLoginError(handleErrorMsg);
  const unsub2 = window.desktopIntegrations.onLogin(handleDesktopLoginCallback);
  const unsub3 = window.desktopIntegrations.onFinishLoginOnMacWithApple?.(
    handleFinishLoginOnMacWithApple,
  );

  return () => {
    unsub1();
    unsub2();
    unsub3?.();
  };
};

const defaultUserHasExistingAccount = (
  lastProvider: AuthPlatform | 'email' | null,
  locationState: { isRegister: boolean } | null,
) => {
  let hasExistingAccount = !!lastProvider;
  if (locationState?.hasOwnProperty?.('isRegister')) {
    hasExistingAccount = !locationState.isRegister;
  }
  return hasExistingAccount;
};

const getViewState = (
  userHasExistingAccount: boolean,
  loginEmailSent: boolean,
  accountNotFound: boolean,
  emailAssociatedWithExistingAccount: boolean,
): ViewState => {
  if (accountNotFound) {
    return 'accountNotFound';
  } else if (emailAssociatedWithExistingAccount) {
    return 'emailAlreadyInUse';
  }
  const accountStatus = userHasExistingAccount ? 'existingAccount' : 'newAccount';
  return loginEmailSent ? 'loginEmailSent' : accountStatus;
};

export function LoginBox({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn(styles.box, className)}>{children}</div>;
}

export function LoginPanelWrapper({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn(styles.login, className)}>{children}</div>;
}

export function LoginFooterLinks({ className }: { className?: string }) {
  return (
    <div className={cn(styles.disclaimer, className)}>
      <FooterLinks />
    </div>
  );
}

export function LoginPanelDisplay({
  className,
  image,
  headline,
  afterBox,
  children,
}: {
  className?: string;
  image: ReactNode;
  headline: ReactNode;
  afterBox?: ReactNode;
  children: ReactNode;
}) {
  return (
    <LoginPanelWrapper className={className}>
      <div className={styles.image}>{image}</div>
      <LoginBox>
        {headline}
        {children}
      </LoginBox>
      {afterBox}
      <LoginFooterLinks />
    </LoginPanelWrapper>
  );
}

export default function LoginPanel({
  className,
  userMessage,
  userSignupMessage,
  copyAfterMessage,
  copyAfterSignupMessage,
}: {
  className?: string;
  userMessage?: string;
  userSignupMessage?: string;
  copyAfterMessage?: ReactNode;
  copyAfterSignupMessage?: ReactNode;
}) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [didMount, setDidMount] = useState(false);
  const [loginEmailSent, setLoginEmailSent] = useState(false);
  const [loginEmailRecipient, setLoginEmailRecipient] = useState('');
  const lastProvider = useMemo(() => previousAuthPlatform(), []);
  const [userHasExistingAccount, setUserHasExistingAccount] = useState<boolean>(
    defaultUserHasExistingAccount(lastProvider, location.state),
  );
  const [signingInWith, setSigningInWith] = useState<'google' | 'apple' | 'email' | null>(null);
  const [loginError, setLoginError] = useState<LoginError | null>(null);
  const [accountNotFound, setAccountNotFound] = useState(false);
  const [emailAssociatedWithExistingAccount, setEmailAssociatedWithExistingAccount] =
    useState(false);

  const toggleUserHasExistingAccount = () => {
    setUserHasExistingAccount(!userHasExistingAccount);
  };

  const viewState = getViewState(
    userHasExistingAccount,
    loginEmailSent,
    accountNotFound,
    emailAssociatedWithExistingAccount,
  );

  let copyAfterHeadline: ReactNode = userHasExistingAccount
    ? 'Welcome back! Sign in to continue.'
    : 'Create your Clay account to continue.';
  copyAfterHeadline =
    (!userHasExistingAccount && copyAfterSignupMessage) || copyAfterMessage || copyAfterHeadline;

  const returnToSignIn = () => {
    setLoginEmailSent(false);
    setAccountNotFound(false);
    setEmailAssociatedWithExistingAccount(false);
  };

  useEffect(() => {
    const signupEmail = searchParams.get('email');
    const marketingEmailSignup = searchParams.get('marketing_email_signup_started');
    if (marketingEmailSignup && signupEmail) {
      setLoginEmailSent(true);
      setLoginEmailRecipient(decodeURIComponent(signupEmail));
      setUserHasExistingAccount(false);
      setSigningInWith('email');
    }
  }, []);

  useEffect(() => {
    if (searchParams.has('signin')) {
      setUserHasExistingAccount(true);
    }
  }, []);

  useEffect(() => {
    if (window.app.isElectron && window.desktopIntegrations) {
      try {
        return attachDesktopLoginHandlers(setLoginError, setSigningInWith);
      } catch (error) {
        return () => {};
      }
    }

    return () => {};
  }, []);

  useEffect(() => {
    const authError = getAuthorizationError();
    if (authError) {
      clearAuthorizationError();
      setLoginError(authError);
    }
  }, []);

  useEffect(() => setDidMount(true), []);

  return (
    <LoginPanelDisplay
      className={cn(className, loginEmailSent && styles.emailSent)}
      image={<Logo className={styles.logo} width={48} height={28} title="Clay" />}
      headline={
        <StateBasedFadeTransition
          state={getViewState(
            userHasExistingAccount,
            loginEmailSent,
            accountNotFound,
            emailAssociatedWithExistingAccount,
          )}
          elementByStateMap={{
            existingAccount: (
              <div>
                <div className={cn(styles.headline, 'sign-in')}>
                  {userMessage || 'Sign in to Clay'}
                </div>
              </div>
            ),
            newAccount: (
              <div>
                <div className={cn(styles.headline, 'sign-up')}>
                  {userSignupMessage || userMessage || 'Sign up for free'}
                </div>
              </div>
            ),
            loginEmailSent: (
              <div>
                <div className={cn(styles.headline, styles.emailConfirmation)}>
                  <img className={styles.envelope} src={envelopeImg} alt="Envelope" />
                  Check your email
                </div>
              </div>
            ),
            accountNotFound: (
              <div>
                <div
                  className={cn(styles.headline, styles.emailConfirmation, styles.accountNotFound)}
                >
                  <img
                    className={styles.missingAccountImg}
                    src={missingAccountImg}
                    alt="Missing account"
                  />
                  Account not found
                </div>
              </div>
            ),
            emailAlreadyInUse: (
              <div>
                <div className={cn(styles.headline, styles.emailConfirmation)}>
                  <img className={styles.envelope} src={envelopeImg} alt="Envelope" />
                  Email already in use
                </div>
              </div>
            ),
          }}
        />
      }
      afterBox={
        <StateBasedFadeTransition
          state={viewState}
          elementByStateMap={{
            existingAccount: (
              <div className={styles.afterBox}>
                <span>
                  Don't have a Clay account?&nbsp;
                  <ToggleButton
                    onClick={toggleUserHasExistingAccount}
                    className={styles.toggleUserHasExistingAccount}
                    disabled={!!signingInWith}
                  >
                    Create account
                  </ToggleButton>
                </span>
              </div>
            ),
            newAccount: (
              <div className={styles.afterBox}>
                <span>
                  Already have a Clay account?&nbsp;
                  <ToggleButton
                    onClick={toggleUserHasExistingAccount}
                    className={styles.toggleUserHasExistingAccount}
                    disabled={!!signingInWith}
                  >
                    Sign in
                  </ToggleButton>
                </span>
              </div>
            ),
            loginEmailSent: (
              <div className={styles.afterBox}>
                Can't find our email? Check your spam folder!
                <span className={styles.toggleText}>
                  <b>←</b>&nbsp;&nbsp;Go back to{' '}
                  <ToggleButton className={styles.backToSignInBtn} onClick={returnToSignIn}>
                    Sign {userHasExistingAccount ? 'in' : 'up'}
                  </ToggleButton>
                </span>
              </div>
            ),
            accountNotFound: (
              <div className={styles.afterBox}>
                <span>
                  <a href={supportHref({ subject: 'Sign in issue - Account not found' })}>
                    Contact us
                  </a>{' '}
                  if you need assistance.
                </span>
              </div>
            ),
            emailAlreadyInUse: (
              <div className={styles.afterBox}>
                <span>
                  <a href={supportHref({ subject: 'Sign in issue - Email already in use' })}>
                    Contact us
                  </a>{' '}
                  if you need assistance.
                </span>
              </div>
            ),
          }}
        />
      }
    >
      <StateBasedFadeTransition
        state={String(loginEmailSent)}
        elementByStateMap={{
          false: (
            <AuthOptions
              signingInWith={signingInWith}
              setSigningInWith={setSigningInWith}
              lastProvider={lastProvider}
              userHasExistingAccount={userHasExistingAccount}
              setLoginEmailSent={setLoginEmailSent}
              setLoginEmailRecipient={setLoginEmailRecipient}
              autoFocusEmailInput={didMount}
              beforeAuthOptions={copyAfterHeadline}
            />
          ),
          true: (
            <EmailConfirmation
              setLoginEmailSent={setLoginEmailSent}
              loginEmailRecipient={loginEmailRecipient}
              userHasExistingAccount={userHasExistingAccount}
              accountNotFound={accountNotFound}
              setAccountNotFound={setAccountNotFound}
              emailAssociatedWithExistingAccount={emailAssociatedWithExistingAccount}
              setEmailAssociatedWithExistingAccount={setEmailAssociatedWithExistingAccount}
            />
          ),
        }}
      />

      <LoginErrorModal
        provider={loginError?.provider}
        message={loginError?.error}
        open={!!loginError}
        onClose={() => setLoginError(null)}
      />
    </LoginPanelDisplay>
  );
}
